/**
 * <pm-audio> — the sound demo player in product descriptions (blocks/richtext.liquid wraps each legacy
 * <audio> in one). Without JS the <audio> keeps its native controls. With JS it becomes a ruled bar:
 * a PLAY / PAUSE button, a position slider and the time. Starting one player pauses the others.
 * Labels come from data-play / data-pause / data-position (locales, product.audio_*).
 */
class PmAudio extends HTMLElement {
  #ac;
  #audio;
  #button;
  #seek;
  #time;
  #seeking = false;

  connectedCallback() {
    this.#audio = this.querySelector('audio');
    if (!this.#audio || this.#button) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const audio = this.#audio;
    audio.removeAttribute('controls');
    audio.preload = 'metadata';

    this.#button = document.createElement('button');
    this.#button.type = 'button';
    this.#button.className = 'pm-audio-btn';
    this.#button.setAttribute('aria-pressed', 'false');
    this.#button.textContent = this.dataset.play || 'Play';

    this.#seek = document.createElement('input');
    this.#seek.type = 'range';
    this.#seek.className = 'pm-audio-seek';
    this.#seek.min = '0';
    this.#seek.max = '1000';
    this.#seek.step = '1';
    this.#seek.value = '0';
    this.#seek.setAttribute('aria-label', this.dataset.position || 'Position');

    this.#time = document.createElement('span');
    this.#time.className = 'pm-audio-time';

    this.append(this.#button, this.#seek, this.#time);
    this.#render();

    this.#button.addEventListener('click', () => (audio.paused ? audio.play() : audio.pause()), opts);
    this.#seek.addEventListener('input', () => {
      this.#seeking = true;
      if (Number.isFinite(audio.duration)) {
        audio.currentTime = (Number(this.#seek.value) / 1000) * audio.duration;
      }
      this.#render();
    }, opts);
    this.#seek.addEventListener('change', () => { this.#seeking = false; }, opts);
    for (const type of ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata', 'durationchange']) {
      audio.addEventListener(type, this.#render, opts);
    }
    audio.addEventListener('play', () => {
      for (const other of document.querySelectorAll('pm-audio audio')) {
        if (other !== audio && !other.paused) other.pause();
      }
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render = () => {
    const audio = this.#audio;
    const playing = !audio.paused && !audio.ended;
    this.#button.textContent = playing ? this.dataset.pause || 'Pause' : this.dataset.play || 'Play';
    this.#button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    if (!this.#seeking && duration) {
      this.#seek.value = String(Math.round((audio.currentTime / duration) * 1000));
    }
    this.#seek.setAttribute('aria-valuetext', PmAudio.#format(audio.currentTime));
    this.#time.textContent = `${PmAudio.#format(audio.currentTime)} / ${duration ? PmAudio.#format(duration) : '–:––'}`;
  };

  static #format(seconds) {
    const s = Math.max(0, Math.floor(seconds || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }
}

if (!customElements.get('pm-audio')) customElements.define('pm-audio', PmAudio);
